import type { PublicBrandDetails } from "@/lib/public-shop-agent-fetch";

// target_audience, popular_categories, and style_identity only contribute when
// the richer client-side BrandIndex response includes them. They are not part
// of the minimal PublicBrandDetails payload used during SSR metadata generation,
// and synthesis skips them when they are undefined.
type BrandCopyFields = Pick<
  PublicBrandDetails,
  "brand_description" | "directory_subheading" | "origin_country" | "founded_year" | "price_tier"
> & {
  target_audience?: string | null;
  popular_categories?: string[] | null;
  style_identity?: string[] | null;
};

const BODY_NEUTRAL_COPY = (brandName: string) =>
  `Explore the latest pieces from ${brandName} on Cosimo.`;
const METADATA_NEUTRAL_COPY = (brandName: string) =>
  `Discover ${brandName} on Cosimo. Shop the latest styles and collections.`;

function toNonEmptyString(value: string | null | undefined): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function formatFacetValue(value: string): string {
  return value.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
}

function joinReadableList(values: string[]): string {
  if (values.length === 1) {
    return values[0];
  }

  if (values.length === 2) {
    return `${values[0]} and ${values[1]}`;
  }

  return `${values.slice(0, -1).join(", ")}, and ${values[values.length - 1]}`;
}

function normalizeArrayValues(values: string[] | null | undefined): string[] {
  return (values ?? [])
    .map((value) => toNonEmptyString(value))
    .filter((value): value is string => Boolean(value))
    .map((value) => formatFacetValue(value))
    .slice(0, 2);
}

function buildSynthesisClauses(
  details: BrandCopyFields | null | undefined,
  surface: "body" | "metadata"
): string[] {
  if (!details) {
    return [];
  }

  const clauses: string[] = [];
  const currentYear = new Date().getFullYear();
  const originCountry = toNonEmptyString(details.origin_country);
  const foundedYearValue = details.founded_year;
  const foundedYear =
    typeof foundedYearValue === "number" && Number.isFinite(foundedYearValue)
      ? Math.trunc(foundedYearValue)
      : null;
  if (originCountry) {
    clauses.push(`is based in ${originCountry}`);
  }

  if (foundedYear && foundedYear > 0 && foundedYear <= currentYear) {
    clauses.push(`was founded in ${foundedYear}`);
  }

  if (surface === "body") {
    return clauses;
  }

  const priceTier = toNonEmptyString(details.price_tier);
  const targetAudience = toNonEmptyString(details.target_audience);
  const popularCategories = normalizeArrayValues(details.popular_categories);
  const styleIdentity = normalizeArrayValues(details.style_identity);

  if (priceTier) {
    clauses.push(`operates in the ${formatFacetValue(priceTier)} price tier`);
  }

  if (targetAudience) {
    clauses.push(`focuses on ${formatFacetValue(targetAudience)}`);
  }

  if (popularCategories.length > 0) {
    clauses.push(`is known for ${joinReadableList(popularCategories)}`);
  }

  if (styleIdentity.length > 0) {
    clauses.push(`leans toward ${joinReadableList(styleIdentity)}`);
  }

  return clauses;
}

function synthesizeBrandCopy(
  brandName: string,
  details: BrandCopyFields | null | undefined,
  surface: "body" | "metadata",
  maxClauses: number
): string | null {
  const normalizedBrandName = brandName.trim();
  const clauses = buildSynthesisClauses(details, surface);

  if (clauses.length < 2) {
    return null;
  }

  const visibleClauses = clauses.slice(0, maxClauses);
  return `${normalizedBrandName} ${joinReadableList(visibleClauses)}.`;
}

// Brand pages should prefer explicit brand facts over generic filler because the
// description copy affects both SEO quality and how trustworthy the page feels.
export function resolveBrandBodyCopy(
  brandDetails: BrandCopyFields | null | undefined,
  brandName: string
): string {
  return (
    toNonEmptyString(brandDetails?.brand_description) ||
    synthesizeBrandCopy(brandName, brandDetails, "body", 3) ||
    BODY_NEUTRAL_COPY(brandName.trim())
  );
}

export function resolveBrandMetaDescription(
  brandDetails: BrandCopyFields | null | undefined,
  brandName: string
): string {
  return (
    toNonEmptyString(brandDetails?.brand_description) ||
    toNonEmptyString(brandDetails?.directory_subheading) ||
    synthesizeBrandCopy(brandName, brandDetails, "metadata", 4) ||
    METADATA_NEUTRAL_COPY(brandName.trim())
  );
}
